import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error as ErrorView } from "@ui/error/Error";
import { Spinner } from "@ui/spinner/Spinner";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import { useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { GD } from "src/GD";
import { formatAmount } from "src/tools/tools";
import { Pagination } from "../conponents/Pagination";
import { NoRecords } from "../providers/NoRecords";
import { SessionAmountCell, SessionFeeCell, SessionIdCell, SessionMerchantCell, SessionReservedCell, SessionStatusCell, SessionTypeCell } from "../forms/Sessions";
import { getStatementTransactionKind, getStatementTransactionsRequest, parseStatementTransactions, STATEMENT_TRANSACTIONS_PAGE_SIZE } from "./statementTransactionData";
import type { StatementTransaction } from "./statementTransactionData";
import { buildStatementTransactionsCsv, loadStatementTransactionsForCsv, STATEMENT_TRANSACTIONS_EXPORT_PAGE_SIZE } from "./statementTransactionsCsv";

type TransactionPage = ReturnType<typeof parseStatementTransactions>;
const columns: { field: keyof StatementTransaction; title: string; width: string }[] = [
	{ field: "id", get title() { return msg("dateId"); }, width: "minmax(135px, max-content)" },
	{ field: "kind", get title() { return msg("kind"); }, width: "minmax(155px, max-content)" },
	{ field: "status", get title() { return msg("status"); }, width: "minmax(155px, max-content)" },
	{ field: "type", get title() { return msg("type"); }, width: "minmax(135px, max-content)" },
	{ field: "mid", get title() { return msg("merchant"); }, width: "minmax(120px, 1fr)" },
	{ field: "rolling_reserve_amount", get title() { return msg("reserved_3385ffe6"); }, width: "max-content" },
	{ field: "fees_amount", get title() { return msg("fee"); }, width: "max-content" },
	{ field: "amount", get title() { return msg("transactionAmount"); }, width: "max-content" },
	{ field: "net", get title() { return msg("net"); }, width: "max-content" },
];

export const StatementTransactionsTab = ({ statementId }: { statementId: number }) => {
	useLocale();
	const [page, setPage] = useState(0);
	const [version, setVersion] = useState(0);
	const [result, setResult] = useState<TransactionPage | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [busy, setBusy] = useState(true);
	const [project] = GD.B_SELECTED_PROJECT.use();
	const exportController = useRef<AbortController | null>(null);
	const [exportBusy, setExportBusy] = useState(false);
	const [exportProgress, setExportProgress] = useState("");
	const [exportError, setExportError] = useState<string | null>(null);
	const serverPage = result?.pagination ? page : 0;

	useEffect(() => {
		setExportBusy(false);
		setExportError(null);
		return () => {
			exportController.current?.abort();
			exportController.current = null;
		};
	}, [statementId, project?.id]);

	const downloadCsv = async () => {
		if (exportController.current || !project) return;
		const controller = new AbortController();
		exportController.current = controller;
		const { signal } = controller;
		const projectId = project.id;
		setExportBusy(true);
		setExportProgress("");
		setExportError(null);
		const checkContext = async () => {
			signal.throwIfAborted();
			const current = await GD.REQ_CURRENT_PROJECT.request();
			signal.throwIfAborted();
			if (current?.id !== projectId) throw new Error(msg("projectChangedPleaseRestartTheExport"));
		};
		try {
			const transactions = await loadStatementTransactionsForCsv(async page => {
				await checkContext();
				const response = await GD.REQ_API.request({
					...getStatementTransactionsRequest(statementId, page, STATEMENT_TRANSACTIONS_EXPORT_PAGE_SIZE),
					expectedProjectId: projectId,
					signal,
				});
				await checkContext();
				if (response.error) throw new Error(response.error.message || msg("couldNotExportStatementTransactions"));
				return response.data;
			}, signal, (loaded, count) => setExportProgress(`${loaded}/${count}`));
			const file = new Blob([buildStatementTransactionsCsv(transactions)], { type: "text/csv;charset=utf-8;" });
			await checkContext();
			saveAs(file, `statement_${statementId}_transactions.csv`);
		} catch (cause) {
			if (!signal.aborted) setExportError(cause instanceof Error ? cause.message : msg("couldNotExportStatementTransactions"));
		} finally {
			if (exportController.current === controller) {
				exportController.current = null;
				setExportBusy(false);
			}
		}
	};

	useEffect(() => {
		let cancelled = false;
		setBusy(true);
		setError(null);
		const load = async () => {
			try {
				const response = await GD.REQ_API.request(getStatementTransactionsRequest(statementId, serverPage));
				if (response.error) throw response.error;
				const parsed = parseStatementTransactions(response.data);
				if (!cancelled) setResult(parsed);
			} catch (cause) {
				if (!cancelled) setError(cause instanceof Error ? cause.message : cause);
			} finally {
				if (!cancelled) setBusy(false);
			}
		};
		void load();
		return () => { cancelled = true; };
	}, [statementId, serverPage, version]);

	const total = result?.pagination?.total ?? result?.rows.length ?? 0;
	const perPage = result?.pagination?.perPage ?? STATEMENT_TRANSACTIONS_PAGE_SIZE;
	const totalPages = Math.ceil(total / perPage);
	const currentPage = Math.min(page, Math.max(0, totalPages - 1));
	const rows = result?.pagination ? result.rows : result?.rows.slice(currentPage * perPage, (currentPage + 1) * perPage) ?? [];
	const refresh = () => { setPage(0); setVersion(value => value + 1); };

	return (
		<VBox gap="12px" sx={{ flex: "1 1 0", width: "100%", minHeight: 0, paddingTop: "16px", overflow: "hidden" }}>
			{error !== null && error !== undefined && <VBox gap="8px"><ErrorView error={error} /><Button title={msg("tryAgain")} variant="secondarySmall" disabled={busy} onClick={() => setVersion(value => value + 1)} /></VBox>}
			{busy ? (
				<VBox center gap="12px" sx={{ flex: 1 }}><Spinner size={28} color="#64748b" /><div role="status">{msg("loadingTransactions_d69971d3")}</div></VBox>
			) : error !== null && error !== undefined ? null : total === 0 ? (
				<NoRecords line1={msg("noTransactionsFound")} line2={msg("thereAreNoTransactionEntriesInThisStatement")} />
			) : (
				<>
					<Box sx={{ flex: "1 1 0", minHeight: 0, overflow: "auto", borderRadius: "8px" }}>
						<Table<StatementTransaction>
							headPadding="14px 12px"
							cellPadding="8px 12px"
							headSx={{ backgroundColor: "#e3dfd6", borderBottom: "none" }}
							sx={{ backgroundColor: "#f8fafc", width: "100%", minWidth: "1100px" }}
							headers={columns.map(column => ({ value: column.field, gridColumn: column.width }))}
							order={columns.map(column => column.field)}
							gridTemplateColumns={columns.map(column => column.width).join(" ")}
							headerFactory={value => ({ content: <Typo sx={{ fontWeight: 400, fontSize: "11px", fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{columns.find(column => column.field === value)?.title ?? value}</Typo> })}
							rowSx={row => getStatementTransactionKind(row).release
								? { "& > div": { backgroundColor: "#f0f7ff" }, "&:hover > div": { backgroundColor: "#e0efff" } }
								: row.status === "failed" ? { "& > div": { backgroundColor: "rgba(239,68,68,.05)" } } : undefined}
							cellFactory={(row, cell, _index, _key, name) => {
								if (name === "id") return { content: <SessionIdCell row={row} /> };
								if (name === "kind") {
									const kind = getStatementTransactionKind(row);
									return { content: <span title={row.kind} style={{ display: "inline-block", borderRadius: "4px", padding: "3px 7px", fontSize: "11px", fontWeight: 600, color: kind.release ? "#1d4ed8" : "#475569", backgroundColor: kind.release ? "#dbeafe" : "#f1f5f9" }}>{kind.label}</span> };
								}
								if (name === "status") return { content: <SessionStatusCell row={row} cell={cell} /> };
								if (name === "type") return { content: <SessionTypeCell row={row} /> };
								if (name === "mid") return { content: <SessionMerchantCell row={row} /> };
								if (name === "amount") return { content: <SessionAmountCell row={row} /> };
								if (name === "rolling_reserve_amount") return { content: <SessionReservedCell row={row} /> };
								if (name === "fees_amount") return { content: <SessionFeeCell row={row} /> };
								if (name === "net") return { content: <Typo sx={{ display: "block", textAlign: "end", whiteSpace: "nowrap", fontWeight: 600, fontSize: "13px", color: "#0f766e" }}>{(row.net === null || row.net === undefined) || row.net === "" || !Number.isFinite(Number(row.net)) ? "—" : `${formatAmount(Number(row.net))} ${row.currency ?? ""}`}</Typo> };
								return { content: null };
							}}
							data={rows}
						/>
					</Box>
				</>
			)}
			{exportError && <ErrorView error={exportError} />}
			<HBox gap="12px" sx={{ alignItems: "center", flexShrink: 0, flexWrap: "wrap", marginTop: "auto" }}>
				{!busy && !error && total > 0 && <Pagination pagination={{ total, page: currentPage, perPage, totalPages }} onPageChange={setPage} showTotal={false} />}
				<HBox gap="28px" sx={{ alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", marginLeft: "auto" }}>
					<Typo bold variant="medium">{msg("statementTransactions")}{result ? ` · ${total}` : ""}</Typo>
					{exportBusy && <span role="status" aria-live="polite">{msg("exporting")}{exportProgress ? ` ${exportProgress}` : "…"}</span>}
					{exportBusy
						? <Button title={msg("cancelExport")} variant="secondarySmall" onClick={() => { exportController.current?.abort(); exportController.current = null; setExportBusy(false); }} />
						: <Button title={msg("downloadCsv")} variant="secondarySmall" disabled={busy || !!error || !total || !project} onClick={downloadCsv} />}
					<Button title={msg("refresh")} variant="secondarySmall" disabled={busy} onClick={refresh} />
				</HBox>
			</HBox>
		</VBox>
	);
};
